import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  MobileSlideDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  CloseIcon,
} from '@/ui';
import DialogScreen from '../../engine/renderer/DialogScreen';
import type { OpenActionSpec } from '@/shared/warnings/types';

type ScreenSchema = Parameters<typeof DialogScreen>[0]['schema'];

interface ProfileFormDialogProps {
  open: boolean;
  title: string;
  schema: ScreenSchema;
  initialData?: Record<string, unknown>;
  formId: string;
  onSubmit: (data: Record<string, unknown>) => void | Promise<void>;
  onClose?: () => void;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  isSaving?: boolean;
  onWarningAction?: (action?: OpenActionSpec, metadata?: { varId?: string }) => boolean | void;
  /**
   * Optional overlay rendered on top of the dialog contents (used for in-dialog confirmations).
   */
  overlay?: React.ReactNode;
}

const ProfileFormDialog: React.FC<ProfileFormDialogProps> = ({
  open,
  title,
  schema,
  initialData,
  formId,
  onSubmit,
  onClose,
  maxWidth = 'sm',
  isSaving = false,
  onWarningAction,
  overlay,
}) => {
  // Track dialog open state to generate unique keys - this forces DialogScreen to remount
  // when the dialog opens, resetting all internal state (including DatePicker open state)
  const [dialogKey, setDialogKey] = useState(0);
  const wasOpenRef = useRef(open);

  useEffect(() => {
    // When dialog transitions from closed to open, increment key to force remount
    if (open && !wasOpenRef.current) {
      setDialogKey((prev) => prev + 1);
    }
    wasOpenRef.current = open;
  }, [open]);

  return (
    <MobileSlideDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
      keepMounted={false}
      PaperProps={{
        sx: { position: 'relative' },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{title}</span>
        <IconButton
          onClick={onClose}
          sx={{ color: '#6b7280' }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ flex: 1, overflow: 'auto' }}>
        <Box sx={{ pt: 1 }}>
          <DialogScreen
            key={dialogKey}
            schema={schema}
            hideHeader
            hideActions
            initialData={initialData || {}}
            onSubmit={(data) => onSubmit(data as Record<string, unknown>)}
            formId={formId}
            onWarningAction={onWarningAction || (() => false)}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #e4e7eb' }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ 
            flex: { xs: 1, md: 'initial' },
            minWidth: { xs: 'auto', md: '140px' },
            py: 1.5,
            borderRadius: 2,
            fontSize: '16px',
            fontWeight: 500,
            textTransform: 'none',
          }}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          type="submit"
          form={formId}
          sx={{ 
            flex: { xs: 1, md: 'initial' },
            minWidth: { xs: 'auto', md: '140px' },
            py: 1.5,
            borderRadius: 2,
            fontSize: '16px',
            fontWeight: 500,
            textTransform: 'none',
            backgroundColor: '#003F44',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#003F44',
              boxShadow: 'none',
              border: '0.5px solid #003F44'
            },
            '&:disabled': {
              backgroundColor: '#E8EFEE',
              color: '#00243E',
            }
          }}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
      {overlay ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 5,
          }}
        >
          {overlay}
        </Box>
      ) : null}
    </MobileSlideDialog>
  );
};

export default ProfileFormDialog;



